import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function clean(s: unknown, max: number): string | null {
  if (typeof s !== "string") return null;
  const v = s.trim();
  if (!v) return null;
  return v.slice(0, max);
}
function cleanEmail(s: unknown): string | null {
  const v = clean(s, 255);
  if (!v || !EMAIL_RE.test(v)) return null;
  return v.toLowerCase();
}
function cleanIds(arr: unknown): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter((x) => typeof x === "string" && x.length < 100).slice(0, 100);
}

const PLACEHOLDER_MESSAGE_PREFIX = "[rating-only]";

interface SubmitPlanRatingInput {
  rating: "yes" | "no";
  eventIds: string[];
  wizardData?: unknown;
  email?: string;
}

/**
 * Saves the current plan, then inserts a placeholder feedback row tagged with
 * the rating + plan share token. Returns the feedback id and share token so
 * the client can update the message later (via updateFeedbackMessage).
 */
export const submitPlanRating = createServerFn({ method: "POST" })
  .inputValidator((input: SubmitPlanRatingInput) => ({
    rating: input?.rating === "no" ? ("no" as const) : ("yes" as const),
    eventIds: cleanIds(input?.eventIds),
    wizardData: input?.wizardData ?? null,
    email: cleanEmail(input?.email),
  }))
  .handler(async ({ data }) => {
    if (data.eventIds.length === 0) {
      return { ok: false as const, error: "No events to rate" };
    }

    // 1. Save the plan
    const { data: plan, error: planError } = await supabaseAdmin
      .from("saved_plans")
      .insert({
        event_ids: data.eventIds,
        wizard_data: (data.wizardData as any) ?? null,
      })
      .select("share_token")
      .single();

    if (planError || !plan?.share_token) {
      return { ok: false as const, error: planError?.message || "Failed to save plan" };
    }

    // 2. Insert a rating-only feedback row
    const { data: feedback, error: feedbackError } = await supabaseAdmin
      .from("site_feedback")
      .insert({
        message: `${PLACEHOLDER_MESSAGE_PREFIX} ${data.rating === "yes" ? "👍" : "👎"}`,
        page_url: `/plan?plan=${plan.share_token}`,
        rating: data.rating,
        plan_share_token: plan.share_token,
        email: data.email,
      } as any)
      .select("id")
      .single();

    if (feedbackError || !feedback?.id) {
      return { ok: false as const, error: feedbackError?.message || "Failed to record rating" };
    }

    return { ok: true as const, feedbackId: feedback.id, shareToken: plan.share_token };
  });

interface UpdateFeedbackMessageInput {
  feedbackId: string;
  message: string;
  email?: string;
}

/**
 * Replaces the placeholder message on a rating-only feedback row with the
 * user's actual comment. Refuses to update rows that are >60 min old, that
 * lack a rating, or whose message is no longer the placeholder (anti-tamper).
 */
export const updateFeedbackMessage = createServerFn({ method: "POST" })
  .inputValidator((input: UpdateFeedbackMessageInput) => ({
    feedbackId: clean(input?.feedbackId, 100) ?? "",
    message: clean(input?.message, 1000) ?? "",
    email: cleanEmail(input?.email),
  }))
  .handler(async ({ data }) => {
    if (!data.feedbackId) return { ok: false as const, error: "Missing feedback id" };
    if (!data.message) return { ok: false as const, error: "Message is required" };

    const { data: existing, error: readError } = await supabaseAdmin
      .from("site_feedback")
      .select("id, rating, message, created_at")
      .eq("id", data.feedbackId)
      .maybeSingle();

    if (readError) return { ok: false as const, error: readError.message };
    if (!existing) return { ok: false as const, error: "Feedback row not found" };

    const cutoff = Date.now() - 60 * 60 * 1000;
    const createdAt = new Date(existing.created_at).getTime();
    if (!Number.isFinite(createdAt) || createdAt < cutoff) {
      return { ok: false as const, error: "Rating too old to attach a comment" };
    }
    if (!(existing as any).rating) {
      return { ok: false as const, error: "Not a rating row" };
    }
    if (typeof existing.message !== "string" || !existing.message.startsWith(PLACEHOLDER_MESSAGE_PREFIX)) {
      return { ok: false as const, error: "Comment already saved for this rating" };
    }

    const { error: updateError } = await supabaseAdmin
      .from("site_feedback")
      .update({
        message: data.message,
        ...(data.email ? { email: data.email } : {}),
      } as any)
      .eq("id", data.feedbackId);

    if (updateError) return { ok: false as const, error: updateError.message };
    return { ok: true as const };
  });